import { useState } from "react";
import { SingleValue } from "react-select";
import { CellContext } from "react-table-8.10.7";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  useAssignExistingMerchantMutation,
  useCreateMerchantMutation,
  useDeleteMerchantFromTxnMutation,
} from "store/apis/merchant";
import type { Transaction, Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import {
  MerchantComponent,
  MerchantSelector,
} from "components/Transaction/MerchantSelector";

export const MerchantColumn = ({
  info,
  onVendorUpdate,
  entityId: entityIdFromProps,
  groupId: groupIdFromProps,
  isDisabled,
}: {
  info: CellContext<Transactions, Transaction["merchant"]>;
  onVendorUpdate: (transactionId: string, isDeleted: boolean) => void;
  entityId: string;
  groupId: string;
  isDisabled?: boolean;
}) => {
  const skipInvalidateTags = [
    "BOOKS_TRANSACTIONS",
    "VENDORS",
    "TRANSACTION_MERCHANTS",
  ];
  const { alertToast } = useToast();
  const { uuid: groupUuid } = useCurrentGroupContext();
  const entityUuid = useCurrentEntityId();
  const { transaction } = info.row.original;
  const { logo, merchant, uuid: transactionId } = transaction;
  const entityId = entityIdFromProps || entityUuid;
  const groupId = groupIdFromProps || groupUuid;

  const [selectedMerchant, setSelectedMerchant] = useState<{
    name: string | null;
    logo: string | null;
  }>(() => ({
    name: merchant || null,
    logo: logo || null,
  }));

  const [assignExistingMerchantToTransaction, { isLoading: isAssigning }] =
    useAssignExistingMerchantMutation();

  const [createMerchant, { isLoading: isCreatingMerchant }] =
    useCreateMerchantMutation();

  const [deleteMerchant, { isLoading: isDeleting }] =
    useDeleteMerchantFromTxnMutation();

  const handleMerchantChange = async (values: SingleValue<any>) => {
    const merchantId = values?.merchantId;

    if (merchantId) {
      const payload = { merchant_id: merchantId };
      try {
        const result = await assignExistingMerchantToTransaction({
          groupId,
          transactionId,
          payload,
          skipInvalidateTags: skipInvalidateTags || [],
        }).unwrap();

        setSelectedMerchant({
          name: result.name,
          logo: result.logo || null,
        });

        onVendorUpdate(transactionId, false);
      } catch (error) {
        setSelectedMerchant({
          name: merchant || null,
          logo: logo || null,
        });
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    } else {
      try {
        await deleteMerchant({
          groupId: groupId,
          transactionId,
          skipInvalidateTags: skipInvalidateTags || [],
        }).unwrap();
        setSelectedMerchant({ name: null, logo: null });
        onVendorUpdate(transactionId, true);
      } catch (error) {
        setSelectedMerchant({
          name: merchant || null,
          logo: logo || null,
        });
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };

  const onCreateMerchant = async (name: string) => {
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("txn_uuid", transactionId);

      const result = await createMerchant({
        groupId: groupId,
        entityId: entityId,
        payload: formData,
        skipInvalidateTags: skipInvalidateTags || [],
      }).unwrap();

      setSelectedMerchant({
        name: result.name,
        logo: result.logo || null,
      });

      onVendorUpdate(transactionId, false);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const value = selectedMerchant.name
    ? {
        value: selectedMerchant.name,
        label: (
          <MerchantComponent
            logo={selectedMerchant.logo}
            name={selectedMerchant.name}
          />
        ),
      }
    : null;

  return (
    <div className="t-group t-select-none" onClick={(e) => e.stopPropagation()}>
      <MerchantSelector
        isDisabled={!!isDisabled}
        customComponent
        size="small"
        onCreateMerchant={onCreateMerchant}
        merchantChange={handleMerchantChange}
        key={transactionId}
        isLoading={isAssigning || isDeleting || isCreatingMerchant}
        defaultValue={value}
        value={value}
        selected={selectedMerchant.name || undefined}
        creatable
        showMerchantDetails={false}
        entityIdFromProps={entityId}
        groupIdFromProps={groupId}
      />
    </div>
  );
};
